import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
  DeleteOutline as DeleteOutlineIcon,
  Folder as FolderIcon,
  InsertDriveFile as InsertDriveFileIcon,
  LinkOff as LinkOffIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

import {
  copyText,
  formatExpiry,
  listShares,
  revokeMany,
  revokeShare,
  Share,
  shareUrl,
} from "./app/shares";
import { logout } from "./app/auth";

const STATE_COLOR: Record<
  Share["state"],
  "success" | "default" | "warning" | "error"
> = {
  active: "success",
  used: "default",
  expired: "warning",
  revoked: "error",
};

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1.25, minWidth: 104 }}>
      <Typography variant="h6" fontWeight={600} lineHeight={1.2}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
}

function AdminPanel({ onExit }: { onExit: () => void }) {
  const [shares, setShares] = useState<Share[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    listShares()
      .then((s) => {
        setShares(s);
        setError(null);
      })
      .catch((e) => setError(e?.message ?? "Could not load links"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(refresh, [refresh]);

  const stats = useMemo(
    () => ({
      total: shares.length,
      active: shares.filter((s) => s.state === "active").length,
      folders: shares.filter((s) => s.isFolder).length,
      downloads: shares.reduce((sum, s) => sum + s.downloads, 0),
    }),
    [shares]
  );

  const filtered = useMemo(
    () =>
      shares.filter((s) => {
        if (stateFilter !== "all" && s.state !== stateFilter) return false;
        if (typeFilter === "folder" && !s.isFolder) return false;
        if (typeFilter === "file" && s.isFolder) return false;
        if (search && !s.key.toLowerCase().includes(search.toLowerCase()))
          return false;
        return true;
      }),
    [shares, stateFilter, typeFilter, search]
  );

  const selectableIds = filtered.map((s) => s.id);
  const allSelected =
    selectableIds.length > 0 && selected.length === selectableIds.length;

  async function runBulk(purge: boolean) {
    const what = purge ? "Delete" : "Turn off";
    if (
      !window.confirm(
        `${what} ${selected.length} link${selected.length === 1 ? "" : "s"}?`
      )
    )
      return;
    setBusy(true);
    const { ok, failed } = await revokeMany(selected, purge);
    setNotice(`${ok} updated${failed ? `, ${failed} failed` : ""}`);
    setSelected([]);
    setBusy(false);
    refresh();
  }

  async function runSingle(share: Share, purge: boolean) {
    const what = purge ? "Delete" : "Turn off";
    if (!window.confirm(`${what} the link for "${share.key}"?`)) return;
    setBusy(true);
    try {
      await revokeShare(share.id, purge);
      refresh();
    } catch (e: any) {
      setError(e?.message ?? "Could not update link");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ height: "100%", overflowY: "auto", pb: 6 }}>
      <Toolbar
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          gap: 1,
          position: "sticky",
          top: 0,
          backgroundColor: "background.paper",
          zIndex: 3,
        }}
      >
        <IconButton edge="start" onClick={onExit} aria-label="Back to drive">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ flexGrow: 1, fontSize: 18 }}>
          Share link admin
        </Typography>
        <IconButton onClick={refresh} aria-label="Refresh">
          <RefreshIcon />
        </IconButton>
        <Button
          size="small"
          onClick={async () => {
            await logout();
            window.location.href = "/";
          }}
        >
          Sign out
        </Button>
      </Toolbar>

      <Box sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setNotice(null)}
          >
            {notice}
          </Alert>
        )}

        <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: "wrap" }}>
          <StatCard label="Links" value={stats.total} />
          <StatCard label="Active" value={stats.active} />
          <StatCard label="Folders" value={stats.folders} />
          <StatCard label="Downloads" value={stats.downloads} />
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />
          <TextField
            select
            size="small"
            label="State"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            sx={{ minWidth: 130 }}
          >
            {["all", "active", "used", "expired", "revoked"].map((v) => (
              <MenuItem key={v} value={v}>
                {v}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            {["all", "file", "folder"].map((v) => (
              <MenuItem key={v} value={v}>
                {v}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {selected.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="body2">{selected.length} selected</Typography>
            <Button
              size="small"
              color="warning"
              startIcon={<LinkOffIcon />}
              disabled={busy}
              onClick={() => runBulk(false)}
            >
              Turn off
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              disabled={busy}
              onClick={() => runBulk(true)}
            >
              Delete
            </Button>
          </Stack>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 6 }}>
            No links match these filters
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={
                        selected.length > 0 && !allSelected
                      }
                      onChange={(e) =>
                        setSelected(e.target.checked ? selectableIds : [])
                      }
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell align="right">Downloads</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((share) => (
                  <TableRow key={share.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected.includes(share.id)}
                        onChange={(e) =>
                          setSelected((prev) =>
                            e.target.checked
                              ? [...prev, share.id]
                              : prev.filter((id) => id !== share.id)
                          )
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {share.isFolder ? (
                          <FolderIcon fontSize="small" color="action" />
                        ) : (
                          <InsertDriveFileIcon fontSize="small" color="action" />
                        )}
                        <Typography variant="body2" noWrap title={share.key}>
                          {share.key.split("/").pop() || share.key}
                        </Typography>
                        {share.oneTime && (
                          <Chip
                            label="1×"
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={share.state}
                        size="small"
                        color={STATE_COLOR[share.state]}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatExpiry(share)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{share.downloads}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" justifyContent="flex-end">
                        <Tooltip title="Copy link">
                          <span>
                            <IconButton
                              size="small"
                              disabled={share.state !== "active"}
                              onClick={async () => {
                                if (await copyText(shareUrl(share.id)))
                                  setNotice("Link copied");
                              }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Turn off now">
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              disabled={busy || share.state !== "active"}
                              onClick={() => runSingle(share, false)}
                            >
                              <LinkOffIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete record">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={busy}
                              onClick={() => runSingle(share, true)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}

export default AdminPanel;
